import { BaseResource } from './base';
import { Course, ListResponse, PaginationParams } from '../types';

export interface CreateCourseData {
    name: string;
    code: string;
    description: string;
    credits: number;
    department: string;
    teacherId: string;
    schedule: {
        days: string[];
        startTime: string;
        endTime: string;
        room: string;
    };
    semester: string;
    maxStudents?: number;
    prerequisites?: string[];
}

export interface UpdateCourseData extends Partial<CreateCourseData> {
    status?: 'active' | 'inactive';
}

export interface CourseFilters extends PaginationParams {
    department?: string;
    semester?: string;
    teacherId?: string;
    status?: 'active' | 'inactive';
    search?: string;
    credits?: number;
}

export class CoursesResource extends BaseResource {
    /**
     * List all courses with optional filtering and pagination
     */
    async list(filters?: CourseFilters): Promise<ListResponse<Course>> {
        return super.requestGet<ListResponse<Course>>('/courses', filters);
    }

    /**
     * Get a specific course by ID
     */
    async get(courseId: string): Promise<Course> {
        return super.requestGet<Course>(`/courses/${courseId}`);
    }

    /**
     * Create a new course
     */
    async create(data: CreateCourseData): Promise<Course> {
        return super.requestPost<Course>('/courses', data);
    }

    /**
     * Update an existing course
     */
    async update(courseId: string, data: UpdateCourseData): Promise<Course> {
        return super.requestPatch<Course>(`/courses/${courseId}`, data);
    }

    /**
     * Delete a course
     */
    async delete(courseId: string): Promise<{ success: boolean; message: string }> {
        return super.requestDelete<{ success: boolean; message: string }>(`/courses/${courseId}`);
    }

    /**
     * Get course enrollments
     */
    async getEnrollments(courseId: string, filters?: PaginationParams): Promise<ListResponse<any>> {
        return super.requestGet<ListResponse<any>>(`/courses/${courseId}/enrollments`, filters);
    }

    /**
     * Get course grades
     */
    async getGrades(courseId: string, filters?: PaginationParams & { studentId?: string }): Promise<ListResponse<any>> {
        return super.requestGet<ListResponse<any>>(`/courses/${courseId}/grades`, filters);
    }

    /**
     * Get course attendance
     */
    async getAttendance(courseId: string, filters?: PaginationParams & { date?: string; status?: string }): Promise<ListResponse<any>> {
        return super.requestGet<ListResponse<any>>(`/courses/${courseId}/attendance`, filters);
    }

    /**
     * Search courses by name, code, or description
     */
    async search(query: string, filters?: Omit<CourseFilters, 'search'>): Promise<ListResponse<Course>> {
        return super.requestGet<ListResponse<Course>>('/courses/search', { q: query, ...filters });
    }
}
